import { useState } from 'react'

export type ColumnLayout = {
  /** Порядок колонок, заданный пользователем. Пусто — порядок по умолчанию. */
  order: string[]
  /** Спрятанные колонки. */
  hidden: string[]
}

const STORAGE_PREFIX = 'meratech:columns:'

/** Список ключей из хранилища: только массив строк, иначе — `null`, и берётся умолчание. */
function readIds(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return null
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return parsed.filter((id): id is string => typeof id === 'string')
  } catch {
    return null
  }
}

function writeIds(key: string, ids: string[] | null): void {
  try {
    if (ids === null) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(ids))
  } catch {
    /* Хранилище недоступно — раскладка живёт до перезагрузки, этого достаточно */
  }
}

/**
 * Раскладка колонок таблицы, которая переживает перезагрузку: порядок, в который пользователь
 * перетащил колонки, и какие он спрятал. Настройка устройства, как ширины (`useColumnWidths`),
 * тема и язык, поэтому `localStorage` под ключом таблицы.
 *
 * Спрятанные хранятся явным списком: ничего не сохранено — действует `defaultHidden`. Колонка,
 * появившаяся позже сохранения, видна. Порядок — список ключей; как собрать из него колонки,
 * решает таблица (`arrangeColumns`). Сброс стирает обе записи.
 */
export function useColumnLayout(tableKey: string, defaultHidden: readonly string[]) {
  const orderKey = `${STORAGE_PREFIX}${tableKey}:order`
  const hiddenKey = `${STORAGE_PREFIX}${tableKey}:hidden`

  const [layout, setLayout] = useState<ColumnLayout>(() => ({
    order: readIds(orderKey) ?? [],
    hidden: readIds(hiddenKey) ?? [...defaultHidden],
  }))

  const setOrder = (order: string[]) => {
    setLayout((prev) => ({ ...prev, order }))
    writeIds(orderKey, order)
  }

  const setHidden = (hidden: string[]) => {
    setLayout((prev) => ({ ...prev, hidden }))
    writeIds(hiddenKey, hidden)
  }

  const reset = () => {
    setLayout({ order: [], hidden: [...defaultHidden] })
    writeIds(orderKey, null)
    writeIds(hiddenKey, null)
  }

  return { ...layout, setOrder, setHidden, reset }
}

/**
 * Колонки в порядке пользователя. Сохранённые ключи идут в сохранённом порядке; колонка, которой
 * в сохранении нет (новая или пришла с другой формой ответа), встаёт за своим соседом слева
 * по умолчанию. Ключи колонок, которых сейчас нет, пропускаются.
 */
export function arrangeColumns<T extends { id: string }>(columns: T[], order: string[]): T[] {
  const byId = new Map(columns.map((column) => [column.id, column]))
  const result = order.flatMap((id) => {
    const column = byId.get(id)
    return column === undefined ? [] : [column]
  })
  columns.forEach((column, index) => {
    if (result.includes(column)) return
    const previous = columns[index - 1]
    const at = previous === undefined ? -1 : result.indexOf(previous)
    result.splice(at + 1, 0, column)
  })
  return result
}
